/* -------------------------------------------------------------------------------------------------
 * Theme Builder Utilities
 * Ported from the HeroUI official theme builder reference (generate-theme-colors.ts).
 * No external dependencies.
 * -----------------------------------------------------------------------------------------------*/

import type {BuilderParams, SemanticColorOverride, SemanticOverrides} from "./builder-constants";

import {fontMap, radiusCssMap} from "./builder-constants";

/* -------------------------------------------------------------------------------------------------
 * Internal Types
 * -----------------------------------------------------------------------------------------------*/

interface OklchColor {
  l: number;
  c: number;
  h: number;
}

export interface ThemeColor {
  name: string;
  oklchLight: string;
  oklchDark: string;
}

export interface GeneratedThemeColors {
  background: ThemeColor;
  foreground: ThemeColor;
  accent: ThemeColor;
  accentForeground: ThemeColor;
  focus: ThemeColor;
  muted: ThemeColor;
  default: ThemeColor;
  defaultForeground: ThemeColor;
  surface: ThemeColor;
  surfaceForeground: ThemeColor;
  surfaceSecondary: ThemeColor;
  surfaceSecondaryForeground: ThemeColor;
  surfaceTertiary: ThemeColor;
  surfaceTertiaryForeground: ThemeColor;
  overlay: ThemeColor;
  overlayForeground: ThemeColor;
  scrollbar: ThemeColor;
  segment: ThemeColor;
  segmentForeground: ThemeColor;
  border: ThemeColor;
  separator: ThemeColor;
  fieldBackground: ThemeColor;
  fieldForeground: ThemeColor;
  fieldPlaceholder: ThemeColor;
  success: ThemeColor;
  successForeground: ThemeColor;
  warning: ThemeColor;
  warningForeground: ThemeColor;
  danger: ThemeColor;
  dangerForeground: ThemeColor;
}

/* -------------------------------------------------------------------------------------------------
 * Constants
 * -----------------------------------------------------------------------------------------------*/

export const DEFAULT_ACCENT = {chroma: 0.195, hue: 253.83, lightness: 0.6204} as const;

const SEMANTIC_HUE_BLEND_FACTOR = 0.12;

const DEFAULT_THEME_VALUES = {
  dark: {
    background: {c: 0.005, h: 285.823, l: 0.12},
    border: {c: 0.006, h: 286.033, l: 0.28},
    default: {c: 0.006, h: 286.033, l: 0.274},
    foreground: {c: 0, h: 0, l: 0.9911},
    muted: {c: 0.015, h: 286.067, l: 0.705},
    overlay: {c: 0.0059, h: 285.89, l: 0.2103},
    scrollbar: {c: 0.015, h: 286.067, l: 0.705},
    segment: {c: 0.01, h: 285.93, l: 0.3964},
    separator: {c: 0.006, h: 286.033, l: 0.25},
    surface: {c: 0.0059, h: 285.89, l: 0.2103},
    surfaceSecondary: {c: 0.0037, h: 286.14, l: 0.257},
    surfaceTertiary: {c: 0.0024, h: 247.91, l: 0.2721},
  },
  light: {
    background: {c: 0, h: 0, l: 0.9702},
    border: {c: 0.004, h: 286.32, l: 0.9},
    default: {c: 0.001, h: 286.375, l: 0.94},
    foreground: {c: 0.0059, h: 285.89, l: 0.2103},
    muted: {c: 0.0138, h: 285.94, l: 0.5517},
    overlay: {c: 0, h: 0, l: 1},
    scrollbar: {c: 0.006, h: 286.286, l: 0.871},
    segment: {c: 0, h: 0, l: 1},
    separator: {c: 0.004, h: 286.32, l: 0.92},
    surface: {c: 0, h: 0, l: 1},
    surfaceSecondary: {c: 0.0013, h: 286.37, l: 0.9524},
    surfaceTertiary: {c: 0.0013, h: 286.37, l: 0.9373},
  },
} as const;

const SEMANTIC_COLORS = {
  danger: {
    chromaDark: 0.1967,
    chromaLight: 0.2328,
    hue: 25.74,
    hueDark: 24.63,
    lightnessDark: 0.594,
    lightnessLight: 0.6532,
  },
  success: {
    chromaDark: 0.1935,
    chromaLight: 0.1935,
    hue: 150.81,
    lightnessDark: 0.7329,
    lightnessLight: 0.7329,
  },
  warning: {
    chromaDark: 0.1388,
    chromaLight: 0.1585,
    hue: 72.33,
    hueDark: 76.34,
    lightnessDark: 0.8203,
    lightnessLight: 0.7819,
  },
} as const;

/* -------------------------------------------------------------------------------------------------
 * Math Helpers
 * -----------------------------------------------------------------------------------------------*/

function formatOklch(color: OklchColor): string {
  return `oklch(${(color.l * 100).toFixed(2)}% ${color.c.toFixed(4)} ${color.h.toFixed(2)})`;
}

function adjustHue(color: OklchColor, newHue: number): OklchColor {
  return {...color, h: newHue};
}

function adjustChroma(color: OklchColor, newChroma: number): OklchColor {
  return {...color, c: Math.max(0, newChroma)};
}

function calculateSemanticHue(semanticDefaultHue: number, baseHue: number): number {
  const hueOffset = (baseHue - DEFAULT_ACCENT.hue) * SEMANTIC_HUE_BLEND_FACTOR;
  let newHue = semanticDefaultHue + hueOffset;

  while (newHue < 0) newHue += 360;
  while (newHue >= 360) newHue -= 360;

  return newHue;
}

export function calculateForeground(oklchString: string): string {
  const match = oklchString.match(/oklch\(([0-9.]+)%?\s+([0-9.]+)\s+([0-9.]+|none)\)/);

  if (!match?.[1] || !match[2] || !match[3]) return "oklch(99.11% 0 0)";
  const l = oklchString.includes("%") ? parseFloat(match[1]) / 100 : parseFloat(match[1]);
  const c = parseFloat(match[2]);
  const h = match[3] === "none" ? 0 : parseFloat(match[3]);
  const normalizedL = l > 1 ? l / 100 : l;

  if (normalizedL > 0.65) return `oklch(15% ${Math.min(c * 0.3, 0.05).toFixed(4)} ${h.toFixed(2)})`;

  return `oklch(98% ${Math.min(c * 0.1, 0.02).toFixed(4)} ${h.toFixed(2)})`;
}

type SemanticColorConfig = (typeof SEMANTIC_COLORS)[keyof typeof SEMANTIC_COLORS];

function generateSemanticColor(
  name: string,
  config: SemanticColorConfig,
  baseHue: number,
  grayChroma: number,
): {color: ThemeColor; foreground: ThemeColor} {
  const adjustedHue = calculateSemanticHue(config.hue, baseHue);
  const adjustedHueDark = calculateSemanticHue(
    "hueDark" in config ? (config.hueDark as number) : config.hue,
    baseHue,
  );
  const chromaBoost = 1 + grayChroma * 2;
  const chromaLight = Math.min(config.chromaLight * chromaBoost, 0.35);
  const chromaDark = Math.min(config.chromaDark * chromaBoost, 0.35);
  const colorLight = formatOklch({c: chromaLight, h: adjustedHue, l: config.lightnessLight});
  const colorDark = formatOklch({c: chromaDark, h: adjustedHueDark, l: config.lightnessDark});
  const LIGHTNESS_THRESHOLD = 0.67;
  const fgLightTheme =
    config.lightnessLight > LIGHTNESS_THRESHOLD
      ? formatOklch({c: 0.0059, h: adjustedHue, l: 0.2103})
      : "oklch(99.11% 0 0)";
  const fgDarkTheme =
    config.lightnessDark > LIGHTNESS_THRESHOLD
      ? formatOklch({c: 0.0059, h: adjustedHueDark, l: 0.2103})
      : "oklch(99.11% 0 0)";

  return {
    color: {name: `--${name}`, oklchDark: colorDark, oklchLight: colorLight},
    foreground: {name: `--${name}-foreground`, oklchDark: fgDarkTheme, oklchLight: fgLightTheme},
  };
}

/* -------------------------------------------------------------------------------------------------
 * Main Color Generation
 * -----------------------------------------------------------------------------------------------*/

interface ColorGenerationParams {
  lightness: number;
  chroma: number;
  hue: number;
  grayChroma?: number;
  semanticOverrides?: SemanticOverrides;
}

export function generateThemeColors(params: ColorGenerationParams): GeneratedThemeColors {
  const {
    lightness,
    chroma,
    hue,
    grayChroma = Math.min(chroma * 0.05, 0.015),
    semanticOverrides,
  } = params;
  const dL = DEFAULT_THEME_VALUES.light;
  const dD = DEFAULT_THEME_VALUES.dark;

  const accentLight = formatOklch({c: chroma, h: hue, l: lightness});
  const accentDark = accentLight;
  const accent: ThemeColor = {name: "--accent", oklchDark: accentDark, oklchLight: accentLight};
  const accentForeground: ThemeColor = {
    name: "--accent-foreground",
    oklchDark:
      semanticOverrides?.dark?.accentForeground ??
      calculateAccentForeground(lightness, chroma, hue),
    oklchLight:
      semanticOverrides?.light?.accentForeground ??
      calculateAccentForeground(lightness, chroma, hue),
  };
  const focus: ThemeColor = {name: "--focus", oklchDark: accentDark, oklchLight: accentLight};
  const background: ThemeColor = {
    name: "--background",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.background, hue), grayChroma)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.background, hue), grayChroma)),
  };
  const foreground: ThemeColor = {
    name: "--foreground",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.foreground, hue), grayChroma)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.foreground, hue), grayChroma)),
  };
  const muted: ThemeColor = {
    name: "--muted",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.muted, hue), grayChroma * 2)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.muted, hue), grayChroma * 2)),
  };
  const defaultColor: ThemeColor = {
    name: "--default",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.default, hue), grayChroma)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.default, hue), grayChroma)),
  };
  const defaultForeground: ThemeColor = {
    name: "--default-foreground",
    oklchDark: "oklch(99.11% 0 0)",
    oklchLight: formatOklch({c: 0.0059, h: hue, l: 0.2103}),
  };
  const surface: ThemeColor = {
    name: "--surface",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.surface, hue), grayChroma * 2)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.surface, hue), grayChroma * 0.5)),
  };
  const surfaceForeground: ThemeColor = {
    name: "--surface-foreground",
    oklchDark: foreground.oklchDark,
    oklchLight: foreground.oklchLight,
  };
  const surfaceSecondary: ThemeColor = {
    name: "--surface-secondary",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.surfaceSecondary, hue), grayChroma * 1.5)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.surfaceSecondary, hue), grayChroma * 0.8)),
  };
  const surfaceSecondaryForeground: ThemeColor = {
    name: "--surface-secondary-foreground",
    oklchDark: foreground.oklchDark,
    oklchLight: foreground.oklchLight,
  };
  const surfaceTertiary: ThemeColor = {
    name: "--surface-tertiary",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.surfaceTertiary, hue), grayChroma * 1.5)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.surfaceTertiary, hue), grayChroma * 0.8)),
  };
  const surfaceTertiaryForeground: ThemeColor = {
    name: "--surface-tertiary-foreground",
    oklchDark: foreground.oklchDark,
    oklchLight: foreground.oklchLight,
  };
  const overlay: ThemeColor = {
    name: "--overlay",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.overlay, hue), grayChroma * 2)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.overlay, hue), grayChroma * 0.3)),
  };
  const overlayForeground: ThemeColor = {
    name: "--overlay-foreground",
    oklchDark: foreground.oklchDark,
    oklchLight: foreground.oklchLight,
  };
  const scrollbar: ThemeColor = {
    name: "--scrollbar",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.scrollbar, hue), grayChroma)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.scrollbar, hue), grayChroma)),
  };
  const segment: ThemeColor = {
    name: "--segment",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.segment, hue), grayChroma)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.segment, hue), grayChroma)),
  };
  const segmentForeground: ThemeColor = {
    name: "--segment-foreground",
    oklchDark: foreground.oklchDark,
    oklchLight: foreground.oklchLight,
  };
  const border: ThemeColor = {
    name: "--border",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.border, hue), grayChroma)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.border, hue), grayChroma)),
  };
  const separator: ThemeColor = {
    name: "--separator",
    oklchDark: formatOklch(adjustChroma(adjustHue(dD.separator, hue), grayChroma)),
    oklchLight: formatOklch(adjustChroma(adjustHue(dL.separator, hue), grayChroma)),
  };
  const fieldBackground: ThemeColor = {
    name: "--field-background",
    oklchDark: surface.oklchDark,
    oklchLight: surface.oklchLight,
  };
  const fieldForeground: ThemeColor = {
    name: "--field-foreground",
    oklchDark: foreground.oklchDark,
    oklchLight: foreground.oklchLight,
  };
  const fieldPlaceholder: ThemeColor = {
    name: "--field-placeholder",
    oklchDark: muted.oklchDark,
    oklchLight: muted.oklchLight,
  };

  // Semantic colors with optional overrides
  function resolveSemanticWithOverride(
    colorName: string,
    config: SemanticColorConfig,
    lightOverride?: SemanticColorOverride,
    darkOverride?: SemanticColorOverride,
  ): {color: ThemeColor; foreground: ThemeColor} {
    if (!lightOverride && !darkOverride) {
      return generateSemanticColor(colorName, config, hue, grayChroma);
    }
    const calc = generateSemanticColor(colorName, config, hue, grayChroma);

    return {
      color: {
        name: `--${colorName}`,
        oklchDark: darkOverride?.color ?? calc.color.oklchDark,
        oklchLight: lightOverride?.color ?? calc.color.oklchLight,
      },
      foreground: {
        name: `--${colorName}-foreground`,
        oklchDark:
          darkOverride?.foreground ??
          calculateForeground(darkOverride?.color ?? calc.color.oklchDark),
        oklchLight:
          lightOverride?.foreground ??
          calculateForeground(lightOverride?.color ?? calc.color.oklchLight),
      },
    };
  }

  const successColors = resolveSemanticWithOverride(
    "success",
    SEMANTIC_COLORS.success,
    semanticOverrides?.light?.success,
    semanticOverrides?.dark?.success,
  );
  const warningColors = resolveSemanticWithOverride(
    "warning",
    SEMANTIC_COLORS.warning,
    semanticOverrides?.light?.warning,
    semanticOverrides?.dark?.warning,
  );
  const dangerColors = resolveSemanticWithOverride(
    "danger",
    SEMANTIC_COLORS.danger,
    semanticOverrides?.light?.danger,
    semanticOverrides?.dark?.danger,
  );

  return {
    accent,
    accentForeground,
    background,
    border,
    danger: dangerColors.color,
    dangerForeground: dangerColors.foreground,
    default: defaultColor,
    defaultForeground,
    fieldBackground,
    fieldForeground,
    fieldPlaceholder,
    focus,
    foreground,
    muted,
    overlay,
    overlayForeground,
    scrollbar,
    segment,
    segmentForeground,
    separator,
    success: successColors.color,
    successForeground: successColors.foreground,
    surface,
    surfaceForeground,
    surfaceSecondary,
    surfaceSecondaryForeground,
    surfaceTertiary,
    surfaceTertiaryForeground,
    warning: warningColors.color,
    warningForeground: warningColors.foreground,
  };
}

export function calculateAccentForeground(lightness: number, chroma: number, hue: number): string {
  if (lightness > 0.65)
    return `oklch(15% ${Math.min(chroma * 0.2, 0.03).toFixed(4)} ${hue.toFixed(2)})`;

  return "oklch(99.11% 0 0)";
}

/* -------------------------------------------------------------------------------------------------
 * CSS Variable Building
 * Ported from get-color-variables-for-element.ts and use-css-sync.ts.
 * -----------------------------------------------------------------------------------------------*/

function getColorVariablesForElement(
  colors: GeneratedThemeColors,
  theme: "light" | "dark",
): Record<string, string> {
  const isLight = theme === "light";
  const get = (color: ThemeColor) => (isLight ? color.oklchLight : color.oklchDark);
  const v = get;

  const vars: Record<string, string> = {};

  vars["--background"] = v(colors.background);
  vars["--color-background"] = v(colors.background);
  vars["--foreground"] = v(colors.foreground);
  vars["--color-foreground"] = v(colors.foreground);

  vars["--accent"] = v(colors.accent);
  vars["--accent-foreground"] = v(colors.accentForeground);
  vars["--focus"] = v(colors.focus);

  vars["--muted"] = v(colors.muted);
  vars["--color-muted"] = v(colors.muted);

  vars["--default"] = v(colors.default);
  vars["--color-default"] = v(colors.default);
  vars["--default-foreground"] = v(colors.defaultForeground);
  vars["--color-default-foreground"] = v(colors.defaultForeground);

  vars["--surface"] = v(colors.surface);
  vars["--color-surface"] = v(colors.surface);
  vars["--surface-foreground"] = v(colors.surfaceForeground);
  vars["--color-surface-foreground"] = v(colors.surfaceForeground);

  vars["--surface-secondary"] = v(colors.surfaceSecondary);
  vars["--color-surface-secondary"] = v(colors.surfaceSecondary);
  vars["--surface-secondary-foreground"] = v(colors.surfaceSecondaryForeground);
  vars["--color-surface-secondary-foreground"] = v(colors.surfaceSecondaryForeground);

  vars["--surface-tertiary"] = v(colors.surfaceTertiary);
  vars["--color-surface-tertiary"] = v(colors.surfaceTertiary);
  vars["--surface-tertiary-foreground"] = v(colors.surfaceTertiaryForeground);
  vars["--color-surface-tertiary-foreground"] = v(colors.surfaceTertiaryForeground);

  vars["--overlay"] = v(colors.overlay);
  vars["--color-overlay"] = v(colors.overlay);
  vars["--overlay-foreground"] = v(colors.overlayForeground);
  vars["--color-overlay-foreground"] = v(colors.overlayForeground);

  vars["--scrollbar"] = v(colors.scrollbar);
  vars["--color-scrollbar"] = v(colors.scrollbar);

  vars["--segment"] = v(colors.segment);
  vars["--color-segment"] = v(colors.segment);
  vars["--segment-foreground"] = v(colors.segmentForeground);
  vars["--color-segment-foreground"] = v(colors.segmentForeground);

  vars["--border"] = v(colors.border);
  vars["--color-border"] = v(colors.border);

  vars["--separator"] = v(colors.separator);
  vars["--color-separator"] = v(colors.separator);

  vars["--field-background"] = v(colors.fieldBackground);
  vars["--field-foreground"] = v(colors.fieldForeground);
  vars["--field-placeholder"] = v(colors.fieldPlaceholder);

  vars["--success"] = v(colors.success);
  vars["--success-foreground"] = v(colors.successForeground);
  vars["--warning"] = v(colors.warning);
  vars["--warning-foreground"] = v(colors.warningForeground);
  vars["--danger"] = v(colors.danger);
  vars["--danger-foreground"] = v(colors.dangerForeground);

  return vars;
}

function getDerivedVariables(
  colors: GeneratedThemeColors,
  theme: "light" | "dark",
): Record<string, string> {
  const isLight = theme === "light";
  const get = (c: ThemeColor) => (isLight ? c.oklchLight : c.oklchDark);
  const accentVal = get(colors.accent);
  const accentFgVal = get(colors.accentForeground);
  const fieldBg = get(colors.fieldBackground);
  const fieldFg = get(colors.fieldForeground);
  const fieldPh = get(colors.fieldPlaceholder);
  const borderVal = get(colors.border);

  const accentDerived: Record<string, string> = {
    "--color-accent": "var(--accent)",
    "--color-accent-foreground": "var(--accent-foreground)",
    "--color-accent-hover": `color-mix(in oklab, ${accentVal} 90%, ${accentFgVal} 10%)`,
    "--color-accent-soft": `color-mix(in oklab, ${accentVal} 15%, transparent)`,
    "--color-accent-soft-foreground": accentVal,
    "--color-accent-soft-hover": `color-mix(in oklab, ${accentVal} 20%, transparent)`,
    "--color-focus": "var(--focus)",
    "--tw-ring-color": "var(--focus)",
  };

  const fieldDerived: Record<string, string> = {
    "--color-field": fieldBg,
    "--color-field-border": borderVal,
    "--color-field-border-focus": `color-mix(in oklab, ${borderVal} 74%, ${fieldFg} 22%)`,
    "--color-field-border-hover": `color-mix(in oklab, ${borderVal} 88%, ${fieldFg} 10%)`,
    "--color-field-focus": fieldBg,
    "--color-field-foreground": fieldFg,
    "--color-field-hover": `color-mix(in oklab, ${fieldBg} 90%, ${fieldFg} 2%)`,
    "--color-field-placeholder": fieldPh,
  };

  const radiusDerived: Record<string, string> = {
    "--radius-2xl": "calc(var(--radius) * 2)",
    "--radius-3xl": "calc(var(--radius) * 3)",
    "--radius-4xl": "calc(var(--radius) * 4)",
    "--radius-field": "var(--field-radius, var(--radius-xl))",
    "--radius-lg": "calc(var(--radius) * 1)",
    "--radius-md": "calc(var(--radius) * 0.75)",
    "--radius-sm": "calc(var(--radius) * 0.5)",
    "--radius-xl": "calc(var(--radius) * 1.5)",
    "--radius-xs": "calc(var(--radius) * 0.25)",
  };

  const semanticNames = ["success", "warning", "danger"] as const;
  const semanticDerived: Record<string, string> = {};

  for (const name of semanticNames) {
    const colorVal = get(colors[name]);
    const fgVal = get(colors[`${name}Foreground`]);

    semanticDerived[`--color-${name}`] = `var(--${name})`;
    semanticDerived[`--color-${name}-foreground`] = `var(--${name}-foreground)`;
    semanticDerived[`--color-${name}-hover`] = `color-mix(in oklab, ${colorVal} 90%, ${fgVal} 10%)`;
    semanticDerived[`--color-${name}-soft`] = `color-mix(in oklab, ${colorVal} 15%, transparent)`;
    semanticDerived[`--color-${name}-soft-foreground`] = colorVal;
    semanticDerived[`--color-${name}-soft-hover`] =
      `color-mix(in oklab, ${colorVal} 20%, transparent)`;
  }

  return {...accentDerived, ...fieldDerived, ...semanticDerived, ...radiusDerived};
}

function buildCssBlock(selector: string, vars: Record<string, string>): string {
  const lines = Object.entries(vars).map(([k, v]) => `  ${k}: ${v};`);

  return `${selector} {\n${lines.join("\n")}\n}`;
}

/**
 * Top-level entry point: generates the full CSS string for the builder preview.
 * This includes :root (light mode) and :root.dark (dark mode) blocks.
 * @param params - The 7 core builder parameters.
 * @param semanticOverrides - Optional brand-specific semantic color overrides (from presets).
 */
export function generateBuilderCSS(
  params: BuilderParams,
  semanticOverrides?: SemanticOverrides,
): string {
  const {base, chroma, fontFamily, formRadius, hue, lightness, radius} = params;
  const grayChroma = Math.min(base, 0.015);

  const colors = generateThemeColors({chroma, grayChroma, hue, lightness, semanticOverrides});

  const radiusCss = radiusCssMap[radius] ?? "0.5rem";
  const formRadiusCss = radiusCssMap[formRadius] ?? "0.5rem";
  const fontConfig = fontMap[fontFamily];
  // Use label (e.g. "DM Sans") not the stripped variable name ("dm-sans")
  const fontCss = fontConfig ? `"${fontConfig.label}", sans-serif` : "sans-serif";

  const sharedRadius = {
    "--field-radius": formRadiusCss,
    "--radius": radiusCss,
  };
  const sharedFont = {"--font-sans": fontCss};

  const lightVars = {
    ...getColorVariablesForElement(colors, "light"),
    ...getDerivedVariables(colors, "light"),
    ...sharedRadius,
    ...sharedFont,
  };

  const darkVars = {
    ...getColorVariablesForElement(colors, "dark"),
    ...getDerivedVariables(colors, "dark"),
    ...sharedRadius,
    ...sharedFont,
  };

  const lightBlock = buildCssBlock(':root, [data-theme="light"]', lightVars);
  const darkBlock = buildCssBlock('.dark, [data-theme="dark"]', darkVars);

  return `${lightBlock}\n\n${darkBlock}`;
}
